import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, Sparkles, ShieldCheck, 
  CreditCard, Landmark, CheckCircle2,
  Search, Zap, Building2, Scale, DollarSign,
  FileCheck, Shield, FileText, Lock, Globe, Stethoscope, Award
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentFeatures, Currency } from '../utils/i18n';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface UsCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: 'en' | 'pl' | 'uk') => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: 'uk' | 'en' | 'pl') => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const US_AGENT_IDS = [
  'us-delaware-corp-architect',
  'us-safe-fundraising-architect',
  'us-fincen-boi-compliance-suite',
  'us-far-sam-procurement-analyst',
  'us-sales-tax-nexus-auditor',
  'us-foreign-llc-form5472-tax-auditor',
  'us-ofac-itar-export-inspector',
  'us-cbp-htsus-customs-optimizer',
  'us-msa-commercial-contracts-counsel',
  'us-flsa-contractor-1099-defense-auditor',
  'us-hipaa-baa-compliance-auditor',
  'us-soc2-nist-security-auditor',
  'us-fda-510k-premarket-validator',
  'us-uspto-trademark-patent-searcher'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Landmark className={className} />;
  return <IconComponent className={className} />;
};

export default function UsCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'USD',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: UsCenterHubViewProps) {
  // Default to English for authentic US corporate standard
  const [activeLang, setActiveLang] = useState<'en' | 'pl' | 'uk'>(language || 'en');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const agents = agentsConfig.filter((a: any) => 
    US_AGENT_IDS.includes(a.id) || 
    a.id.startsWith('us-') || 
    a.categoryEn === 'US HUB & CORPORATE LAW' || 
    a.categoryPl === 'CENTRUM USA & PRAWO KORPORACYJNE' ||
    a.id.includes('delaware')
  );

  const categories = [
    { id: 'all', label: isPl ? `Wszystkie moduły USA (${agents.length})` : isUk ? `Усі модулі США (${agents.length})` : `All US Modules (${agents.length})` },
    { id: 'corporate_vc', label: isPl ? 'Delaware, SAFE & BOI' : isUk ? 'Delaware, SAFE & BOI' : 'Corporate, SAFE & BOI' },
    { id: 'gov_far', label: isPl ? 'SAM.gov & FAR / DFARS' : isUk ? 'SAM.gov & FAR / DFARS' : 'GovTech & FAR Procurement' },
    { id: 'tax_irs', label: isPl ? 'Podatki IRS & Sales Tax' : isUk ? 'Податки IRS & Sales Tax' : 'IRS Tax & Multi-State Nexus' },
    { id: 'trade_customs', label: isPl ? 'OFAC, ITAR & HTSUS CBP' : isUk ? 'OFAC, ITAR & HTSUS CBP' : 'OFAC, ITAR & Customs CBP' },
    { id: 'contracts_hr', label: isPl ? 'MSA, SLA & 1099 vs W-2' : isUk ? 'MSA, SLA & 1099 vs W-2' : 'Contracts & 1099 vs W-2' },
    { id: 'cyber_health_ip', label: isPl ? 'HIPAA, SOC 2, FDA & USPTO' : isUk ? 'HIPAA, SOC 2, FDA & USPTO' : 'Cyber, FDA & USPTO' }
  ];

  const filteredAgents = agents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || name.includes(query) || desc.includes(query);

    if (!matchesSearch) return false;
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'corporate_vc') return ['us-delaware-corp-architect', 'us-safe-fundraising-architect', 'us-fincen-boi-compliance-suite'].includes(agent.id);
    if (selectedCategory === 'gov_far') return ['us-far-sam-procurement-analyst'].includes(agent.id);
    if (selectedCategory === 'tax_irs') return ['us-sales-tax-nexus-auditor', 'us-foreign-llc-form5472-tax-auditor'].includes(agent.id);
    if (selectedCategory === 'trade_customs') return ['us-ofac-itar-export-inspector', 'us-cbp-htsus-customs-optimizer'].includes(agent.id);
    if (selectedCategory === 'contracts_hr') return ['us-msa-commercial-contracts-counsel', 'us-flsa-contractor-1099-defense-auditor'].includes(agent.id);
    if (selectedCategory === 'cyber_health_ip') return ['us-hipaa-baa-compliance-auditor', 'us-soc2-nist-security-auditor', 'us-fda-510k-premarket-validator', 'us-uspto-trademark-patent-searcher'].includes(agent.id);
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono max-w-7xl mx-auto">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Native Language Switcher for US Hub */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 border border-slate-700 rounded-lg">
            <button
              onClick={() => {
                setActiveLang('en');
                if (onSelectLanguage) onSelectLanguage('en');
                if (onSelectCurrency) onSelectCurrency('USD');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'en' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇺🇸 English
            </button>
            <button
              onClick={() => {
                setActiveLang('uk');
                if (onSelectLanguage) onSelectLanguage('uk');
                if (onSelectCurrency) onSelectCurrency('UAH');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'uk' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇺🇦 Українська
            </button>
            <button
              onClick={() => {
                setActiveLang('pl');
                if (onSelectLanguage) onSelectLanguage('pl');
                if (onSelectCurrency) onSelectCurrency('PLN');
              }}
              className={`px-2.5 py-1 text-[11px] font-bold rounded cursor-pointer transition-all ${activeLang === 'pl' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              🇵🇱 Polski
            </button>
          </div>

          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-rose-500/20 text-rose-300 border border-rose-400/30 rounded-md flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-rose-400" />
            <span>DELAWARE • FAR • IRS • OFAC • SOC 2 • FDA</span>
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 border-2 border-blue-600/60 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-500/20 border border-rose-400/40 rounded-full text-[11px] font-bold text-rose-300 uppercase tracking-wider">
            <span>🇺🇸 UNITED STATES • FEDERAL & STATE CORPORATE, TAX & COMPLIANCE SUITE</span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-3">
            <span>{isPl ? 'CENTRUM OPERACYJNE USA & COMPLIANCE' : isUk ? 'ОПЕРАЦІЙНИЙ ЦЕНТР США & КОМПЛАЄНС' : 'US ENTERPRISE, LEGAL & COMPLIANCE HUB'}</span>
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl
              ? 'Wyspecjalizowany ekosystem prawny i podatkowy dla firm operujących na rynku USA: tworzenie spółek Delaware LLC/C-Corp (DGCL), modelowanie rund inwestycyjnych YC SAFE, raportowanie beneficjentów FinCEN BOI (CTA), zamówienia federalne SAM.gov FAR/DFARS, audyt podatku IRS Form 5472 i Sales Tax Nexus, sankcje OFAC i ITAR, umowy B2B MSA/SLA, audyt HIPAA i gotowość do certyfikacji SOC 2 Type II.'
              : isUk
              ? 'Спеціалізована юридична, податкова та регуляторна платформа для бізнесу на ринку США: створення та структурування Delaware LLC/C-Corp (DGCL), інвестиційні угоди YC SAFE та Cap Table, звітність бенефіціарів FinCEN BOI (CTA), федеральні держзакупівлі SAM.gov FAR/DFARS, податковий аудит нерезидентів IRS Form 5472 та Sales Tax Nexus, санкції OFAC та експортний контроль ITAR/EAR, договори B2B MSA/SLA, захист медичних даних HIPAA та аудит готовності SOC 2 Type II.'
              : 'End-to-end legal, tax, and regulatory operating suite for businesses and startups entering or expanding across the United States: Delaware LLC & C-Corp incorporation (DGCL), YC Post-Money SAFE modeling, FinCEN BOI Beneficial Ownership compliance (CTA), SAM.gov FAR/DFARS federal proposals, IRS Form 5472 & Multi-State Sales Tax Nexus audits, OFAC/ITAR sanctions screening, B2B MSA contracts, HIPAA security, and SOC 2 Type II audit readiness.'}
          </p>

          {/* Quick Hub Pass Promo */}
          <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-900/95 border border-blue-500/50 rounded-xl">
            <div className="space-y-1">
              <div className="text-xs font-bold text-blue-300 uppercase flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-rose-400" />
                <span>{isPl ? 'PAKIET KOMPLEKSOWY: US ALL-ACCESS HUB PASS' : isUk ? 'КОМПЛЕКСНИЙ ПАКЕТ: US ALL-ACCESS HUB PASS' : 'ALL-INCLUSIVE: US ENTERPRISE HUB PASS'}</span>
              </div>
              <div className="text-[11px] text-slate-300">
                {isPl
                  ? 'Dostęp do wszystkich 14 modułów USA (200 zapytań/miesiąc) • Stripe / Apple Pay / Cards / ACH'
                  : isUk
                  ? 'Повний доступ до всіх 14 модулів США (200 запитів/міс) • Stripe / Apple Pay / Cards / ACH'
                  : 'Full access to all 14 US modules (200 requests/month) • Stripe / Apple Pay / Cards / ACH'}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl font-extrabold text-white font-mono">$399<span className="text-xs text-blue-300 font-normal"> / {isPl ? 'miesiąc' : isUk ? 'міс' : 'mo'}</span></span>
              <button
                onClick={() => handleOpenPayment('us-hub-pass', isPl ? 'Centrum USA (Hub Pass)' : isUk ? 'Центр США (Hub Pass)' : 'US Hub Pass', 399, 39)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase rounded-lg transition-all shadow-md shadow-rose-900/40 cursor-pointer flex items-center gap-1.5"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>{isPl ? 'Aktywuj Pakiet' : isUk ? 'Активувати Хаб' : 'Activate Pass'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isPl ? 'Szukaj modułu USA...' : isUk ? 'Пошук модуля США...' : 'Search US module...'}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono uppercase whitespace-nowrap transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map(agent => {
          const name = getLocalizedAgentName(agent, activeLang);
          const desc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);

          // Pricing according to 98% margin US rule
          let priceUsd = agent.subscriptionPriceUsd || 79;
          let singlePriceUsd = agent.singlePriceUsd || 15;
          if (['us-ofac-itar-export-inspector', 'us-cbp-htsus-customs-optimizer', 'us-fda-510k-premarket-validator', 'us-soc2-nist-security-auditor'].includes(agent.id)) {
            priceUsd = 149;
            singlePriceUsd = 29;
          } else if (['us-msa-commercial-contracts-counsel', 'us-uspto-trademark-patent-searcher'].includes(agent.id)) {
            priceUsd = 29;
            singlePriceUsd = 5;
          }

          return (
            <div
              key={agent.id}
              className="bg-white border border-slate-200 hover:border-blue-500/70 rounded-2xl p-6 shadow-xs hover:shadow-xl transition-all flex flex-col justify-between gap-5 relative group font-mono"
            >
              <div className="space-y-4">
                {/* Header Badge */}
                <div className="flex items-center justify-between gap-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div 
                    onClick={() => handleOpenPayment(agent.id, name, priceUsd, singlePriceUsd)}
                    className="text-right cursor-pointer hover:opacity-85 transition-opacity bg-blue-50/70 hover:bg-blue-100/90 p-2 rounded-xl border border-blue-200/70"
                    title={isPl ? 'Kliknij, aby opłacić pakiet' : isUk ? 'Натисніть для вибору тарифу' : 'Click to select pricing plan'}
                  >
                    <div className="text-xs font-extrabold text-slate-900 font-mono">
                      ${singlePriceUsd} <span className="text-[10px] text-slate-500 font-normal">/ {isPl ? 'uruchomienie' : isUk ? 'запуск' : 'run'}</span>
                    </div>
                    <div className="text-[10px] text-blue-600 font-bold font-mono">
                      💳 ${priceUsd} / 30 {isPl ? 'zapytań' : isUk ? 'запитів' : 'req'}
                    </div>
                  </div>
                </div>

                {/* Title & Desc */}
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 uppercase tracking-tight group-hover:text-blue-600 transition-colors leading-snug">
                    {name}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed mt-2 line-clamp-3">
                    {desc}
                  </p>
                </div>

                {/* Feature List */}
                <ul className="space-y-1.5 text-[11px] text-slate-700 border-t border-slate-100 pt-3">
                  {features.slice(0, 3).map((f: string, i: number) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-1.5" />
                      <span className="line-clamp-1">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons: Open Module + Buy Subscription */}
              <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
                <button
                  onClick={() => onLaunchAgent(agent.target || agent.id, activeLang)}
                  className="flex-1 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs group-hover:shadow-md"
                >
                  <span>{isPl ? '🚀 Otwórz' : isUk ? '🚀 Відкрити' : '🚀 Open'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleOpenPayment(agent.id, name, priceUsd, singlePriceUsd)}
                  className="py-3 px-3.5 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs hover:shadow-blue-600/30"
                  title={isPl ? 'Opłać subskrypcję lub pojedyncze zapytanie' : isUk ? 'Оплатити підписку або разовий запуск' : 'Buy subscription or single run'}
                >
                  <CreditCard className="w-4 h-4" />
                  <span>{isPl ? 'Kup' : isUk ? 'Оплатити' : 'Buy'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* US Payment Methods & Trust Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-400" />
            <h4 className="text-xs sm:text-sm font-extrabold uppercase text-blue-200">
              {isPl ? '💳 Bezpieczne Płatności i Rozliczenia w USA (USD)' : isUk ? '💳 Безпечні Платежі та Розрахунки в США (USD)' : '💳 Secure US Payments & Corporate Invoicing (USD)'}
            </h4>
          </div>
          <button
            onClick={() => handleOpenPayment('us-hub-pass', isPl ? 'Centrum USA (Hub Pass)' : isUk ? 'Центр США (Hub Pass)' : 'US Hub Pass', 399, 39)}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-md"
          >
            <Sparkles className="w-4 h-4" />
            <span>{isPl ? '👑 Kup US Hub Pass ($399)' : isUk ? '👑 Купити US Hub Pass ($399)' : '👑 Buy US Hub Pass ($399)'}</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">💳 US Credit & Debit Cards</div>
            <div className="text-[10px] text-slate-400">Visa / MC / Amex / Discover</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">🏦 ACH Direct Debit</div>
            <div className="text-[10px] text-slate-400">US Bank to Bank Transfer</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">🍏 Apple & Google Pay</div>
            <div className="text-[10px] text-slate-400">1-click express checkout</div>
          </div>
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center space-y-1">
            <div className="text-xs font-bold text-white">🏢 US Corporate Invoicing</div>
            <div className="text-[10px] text-slate-400">W-9 / EIN Form Support</div>
          </div>
        </div>
      </div>

      {/* Regional Payment Modal */}
      {selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          region="US"
          language={activeLang}
          defaultPrice={selectedPaymentAgent.price}
          defaultSinglePrice={selectedPaymentAgent.singlePrice}
        />
      )}
    </div>
  );
}
